/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";

// region Imports - Graphql
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  concretingRequestAllWithSortedPagination,
  TConcretingRequestAllWithSortedPaginationQueries
} from "@graphql/groups/concretingRequest/query.graphql";
import {
  ConcretingRequestQueriesGetAllWithSortedPaginationArgs,
  ConcretingRequestType,
  ECommandResultStatus,
  ESort,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Imports - Graphql
// region Imports - Telluria Utils
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Imports - Telluria Utils
// region Imports - Assets
import { menuConcretingRequest } from "@assets/applications";
// endregion Imports - Assets
// region Imports - Languages
import useTranslation from "@languages/useTranslation";
import { AlertMessages, ConcretingRequestMessages, CustomerMessages } from "@languages/interfaces";
// endregion Imports - Languages
// region Imports - Hooks
import { useAuth } from "src/hooks/useAuth";
import { useSelectClient } from "@atoms/SelectClient";
// endregion Imports - Hooks
// region Imports - Stores
import Store from "@store/jotai";
import { ModulesID } from "@store/constants/index";
// endregion Imports - Stores
// region Imports - Libraries
import { Grid } from "@libraries/mui";
// endregion Imports - Libraries
// region Imports - Helpers
import utils from "@helpers/utils";
// endregion Imports - Helpers
// region Imports - Atoms
import AlertDialog from "@atoms/AlertDialog";
import AlertContentByClient from "@atoms/AlertContentByClient";
import { Loading } from "@atoms/index";
import ShowMoreButton from "@atoms/ShowMoreButton";
// endregion Imports - Atoms
// region Imports - Molecules
import type {
  IConcretingRequestConcreteRecipeData,
  IConcretingRequestConcretingData,
  IConcretingRequestPumpData
} from "@molecules/index";
import { BaseCard, BaseCardAction, BaseCardHeader, BaseCardItem, PageHeader } from "@molecules/index";
// endregion Imports - Molecules
// region Imports - Pages
import Office from "@pages/Office";
// endregion Imports - Pages
// region Imports - Styles
import * as Styled from "./styles";
// endregion Imports - Styles

// region Interfaces
interface WhereContractIdsByClient {
  clientId: string;
  contractIds: string;
}
// endregion Interfaces

const ConcretingRequests: React.FC = () => {

  // region Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const {
    value: concretingRequests,
    setValue: setConcretingRequests,
    filteredConcretingRequests,
    setFilteredConcretingRequests
  } = Store.useConcretingRequests();
  const { value: selectedClient } = useSelectClient();
  const { getDecodedToken } = useAuth();
  // endregion Hooks
  // region States
  const [whereContractIdsByClient, setWhereContractIdsByClient] = useState<WhereContractIdsByClient[]>([]);

  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [alertDialogMessage, setAlertDialogMessage] = useState<string>("");
  const [isPermissionDialogAlert, setIsPermissionDialogAlert] = useState<boolean>(false);

  const [noPermissionInCurrentClient, setNoPermissionInCurrentClient] = useState<boolean>(false);
  const [noDataInCurrentClient, setNoDataInCurrentClient] = useState<boolean>(false);
  // endregion States
  // region Constants
  const limitPerPage = 9;
  const token = getDecodedToken();
  // endregion Constants
  // region GraphQL - Queries
  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.id", "construction.client.id"]), {
    fetchPolicy: "network-only",
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONCRETING_REQUEST
        }
      ]
    }
  });

  const [
    getConcretingRequests,
    {
      loading: loadingConcretingRequests,
      error: errorConcretingRequests,
      data: dataConcretingRequests
    }
  ] = useLazyQuery<
    TConcretingRequestAllWithSortedPaginationQueries,
    ConcretingRequestQueriesGetAllWithSortedPaginationArgs
  >(
    concretingRequestAllWithSortedPagination([
      "id",
      "status",
      "requestedDate",
      "requestedQuantity",
      "quantityPerConcreteMixer",
      "toConfirm",
      "travelsReleased",
      "interval",
      "pieceToBeConcreted",
      "pump",
      "observationPump",
      "observationConcreting",
      "requester",
      "requesterPhoneNumber",
      "concreteBatchingPlant.name",
      "contract.id",
      "contract.code",
      "contract.status",
      "contract.contractDate",
      "contractConcreteRecipe.id",
      "contractConcreteRecipe.concreteRecipe.description",
      "contractConcreteRecipe.totalVolume",
      "contractConcreteRecipe.deliveredVolume",
      "contract.construction.name",
      "contract.construction.address",
      "contract.construction.county",
      "contract.construction.number",
      "contract.construction.complement",
      "contract.contractSellers.seller.name",
      "messages.id"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );
  // endregion GraphQL - Queries
  // region Functions
  const handleNewConcretingRequest = useCallback(() => {
    if (selectedClient.pendency) {
      addToast({
        type: "error",
        title: t(ConcretingRequestMessages.clientWithPendencyText)
      });

      return;
    }
    navigate("/concreting-request");
  }, [navigate, selectedClient, addToast, t]);

  const loadConcretingRequests = useCallback(async () => {
    const response = dataConcretingRequests?.concretingRequest.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setTotalPages(response.pageCount);
      const concretingRequestsList = response.result;

      if (nextPage > 1) {
        setConcretingRequests(concretingRequests.concat(concretingRequestsList as ConcretingRequestType[]));
      } else {
        setConcretingRequests(
          concretingRequestsList as ConcretingRequestType[]
        );
      }
    }
  }, [dataConcretingRequests, setConcretingRequests]);

  /**
   * Make the where clause to get the specified concreting requests according to user permissions
   */
  const makeWhereContractIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      if (response.result?.length === 0) {
        setWhereContractIdsByClient([]);

        setIsPermissionDialogAlert(true);
        setAlertDialogMessage(t(AlertMessages.noPermissionConcretingRequest));
        setAlertDialogOpen(true);

        return;
      }

      const whereContractIdsByClients: WhereContractIdsByClient[] = [];

      response.result?.forEach((permission) => {
        if (permission?.contract?.id) {

          const whereContractIdsByClient = whereContractIdsByClients.find(
            (whereContractIdsByClient) => whereContractIdsByClient.clientId === permission.construction?.client?.id
          );

          if (whereContractIdsByClient) {
            // eslint-disable-next-line function-paren-newline
            whereContractIdsByClient.contractIds = utils.concatWithSeparator(
              whereContractIdsByClient.contractIds, permission?.contract?.id, "|");
          } else {
            whereContractIdsByClients.push({
              clientId: permission?.construction?.client?.id as string,
              contractIds: permission?.contract?.id
            });
          }
        }
      });

      setWhereContractIdsByClient(whereContractIdsByClients);
    }
  }, [dataPermissions, setFilteredConcretingRequests]);

  const handleNextPage = () => setNextPage(nextPage + 1);

  const concretingRequestDetails = useCallback(
    (concretingRequest: ConcretingRequestType) => {
      const concretingRequestConcreting: IConcretingRequestConcretingData = {
        status: concretingRequest.status,
        requestDate: concretingRequest.requestedDate,
        concreteBatchingPlantName:
          concretingRequest.concreteBatchingPlant?.name ?? "",
        // toConfirm: concretingRequest.toConfirm,
        // travelsReleased: concretingRequest.travelsReleased ?? undefined,
        interval: concretingRequest.interval,
        observationConcreting: concretingRequest.observationConcreting ?? "",
        requester: concretingRequest.requester,
        requesterPhoneNumber: concretingRequest.requesterPhoneNumber
      };

      const concretingRequestConcreteRecipe: IConcretingRequestConcreteRecipeData = {
        concreteRecipeDescription:
          concretingRequest.contractConcreteRecipe?.concreteRecipe
            ?.description ?? "",
        quantityPerConcreteMixer: concretingRequest.quantityPerConcreteMixer,
        pieceToBeConcreted: concretingRequest.pieceToBeConcreted ?? undefined,
        requestedQuantity: concretingRequest.requestedQuantity,
        status: concretingRequest.status
      };

      const concretingRequestPump: IConcretingRequestPumpData = {
        pump: concretingRequest.pump,
        pipingDistance: concretingRequest.pipingDistance,
        observationPump: concretingRequest.observationPump ?? undefined,
        status: concretingRequest.status
      };

      return (
        <div>
          <BaseCardItem
            type="concretingRequestConcreting"
            concretingRequestConcreting={concretingRequestConcreting}
          />
          <BaseCardItem
            type="concretingRequestConcreteRecipe"
            concretingRequestConcreteRecipe={concretingRequestConcreteRecipe}
          />
          <BaseCardItem
            type="concretingRequestPump"
            concretingRequestPump={concretingRequestPump}
          />
        </div>
      );
    },
    []
  );

  // endregion Functions
  // region UseEffects
  /**
   * Reset contracts when selected client changes
   */
  useEffect(() => {
    setConcretingRequests([]);
    setNextPage(1);
    setTotalPages(0);
    setNoPermissionInCurrentClient(false);
    setNoDataInCurrentClient(true);
  }, [selectedClient, setConcretingRequests]);

  /**
   * Execute contract query when selected client changes
   */
  useEffect(() => {
    const whereContractsByClient = whereContractIdsByClient.find(
      (contractByClient) => contractByClient.clientId === selectedClient?.id
    );

    if (!whereContractsByClient) {
      setNoPermissionInCurrentClient(true);

      return;
    }

    setNoPermissionInCurrentClient(false);

    const whereContractIds = whereContractsByClient?.contractIds;

    if (whereContractIds === "") {
      setNoDataInCurrentClient(true);

      return;
    }

    getConcretingRequests({
      variables: {
        page: nextPage,
        perPage: limitPerPage,
        sort: [{
          field: "requestedDate",
          sortDirection: ESort.DESC
        }],
        where: [
          {
            property: "contract.id",
            operator: EWhereClausesOperators.IN,
            value: whereContractIds
          }
        ]
      }
    });
  }, [getConcretingRequests, whereContractIdsByClient, nextPage, selectedClient]);

  /**
   * Process invoice permissions to get contracts ids by client
   */
  useEffect(() => {
    if (dataPermissions) makeWhereContractIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereContractIds]);

  /**
   * Process invoices query response when contracts query changes
   */
  useEffect(() => {
    if (dataConcretingRequests) loadConcretingRequests();
    if (errorConcretingRequests) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorConcretingRequests.message
      });
    }
  }, [
    addToast,
    dataConcretingRequests,
    errorConcretingRequests,
    loadConcretingRequests
  ]);

  /**
   * Set filtered concreting requests and check if there is data to show
   */
  useEffect(() => {
    setFilteredConcretingRequests(concretingRequests);

    if (concretingRequests?.length > 0) {
      setNoDataInCurrentClient(false);
    } else {
      setNoDataInCurrentClient(true);
    }
  }, [concretingRequests, setFilteredConcretingRequests]);
  // endregion UseEffects

  return (
    <Office>
      <PageHeader
        url="/"
        title={t(CustomerMessages.applicationConcretingRequest)}
      />

      <Styled.Container>
        <AlertDialog
          open={alertDialogOpen}
          title={alertDialogMessage}
          onClose={() => setAlertDialogOpen(false)}
          iconColor={isPermissionDialogAlert ? "red" : "yellow"}
        />
        <Grid container spacing={2}>
          {(loadingConcretingRequests || loadingPermissions) && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!loadingPermissions
            && !loadingConcretingRequests
            && filteredConcretingRequests
            && filteredConcretingRequests.map((concretingRequest) => (
              <Grid
                key={`grid-${concretingRequest.id}`}
                item
                xs={12}
                sm={6}
                md={4}
              >
                <BaseCard
                  key={concretingRequest.id}
                  id={concretingRequest.id}
                  header={
                    <BaseCardHeader
                      type="concretingRequest"
                      data={concretingRequest}
                    />
                    }
                  details={concretingRequestDetails(concretingRequest)}
                  actions={
                    <BaseCardAction
                      type="concretingRequest"
                      concretingRequest={concretingRequest}
                    />
                    }
                />
              </Grid>
            ))}
          {!loadingPermissions && !loadingConcretingRequests && (noPermissionInCurrentClient || noDataInCurrentClient) && (
            <AlertContentByClient
              alertMessage={noPermissionInCurrentClient
                ? t(AlertMessages.noPermissionConcretingRequestSpecificClient)
                : t(AlertMessages.noHaveConcretingRequestSpecificClient)}
              iconColor={noPermissionInCurrentClient ? "red" : "yellow"}
            />
          )}
        </Grid>
        {filteredConcretingRequests
          && nextPage < totalPages
          && (
          <Styled.ContainerShowMoreButton>
            <ShowMoreButton onClick={handleNextPage} />
          </Styled.ContainerShowMoreButton>
          )}
      </Styled.Container>

      {!noPermissionInCurrentClient && (
        <Styled.LinkContractButtonContent>
          <Styled.LinkContractButton
            variant="contained"
            size="small"
            type="submit"
            text={t(
              ConcretingRequestMessages.requestNewConcretingText
            ).toUpperCase()}
            endIcon={<ReactSVG src={menuConcretingRequest} />}
            onClick={handleNewConcretingRequest}
          />
        </Styled.LinkContractButtonContent>
      )}

    </Office>
  );
};

export default ConcretingRequests;
